// #region Imports
import { defineComponent } from "vue";
// #endregion
// #region Constanten
const cache = {};
// #endregion
// #region Functions
function makePromiseState(promise) {
    // Don't modify any promise that has been already modified.
    if (promise.isPending)
        return promise;
    // Set initial state
    let isPending = true;
    // Observe the promise, saving the fulfillment in a closure scope.
    let result = promise.then((v) => {
        isPending = false;
        return v;
    }, (e) => {
        isPending = false;
        throw e;
    });
    result.isPending = function getIsPending() { return isPending; };
    return result;
}
function setTitle(svg, title) {
    const titleTags = svg.getElementsByTagName('title');
    if (titleTags.length) { // overwrite existing title
        titleTags[0].textContent = title;
    }
    else { // create a title element if one doesn't already exist
        const titleEl = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        titleEl.textContent = title;
        svg.appendChild(titleEl);
    }
}
function filterAttrs(attrs) {
    return Object.keys(attrs).reduce((result, key) => {
        if (attrs[key] !== false && attrs[key] !== null && attrs[key] !== undefined) {
            result[key] = attrs[key];
        }
        return result;
    }, {});
}
// #endregion
export default defineComponent({
    // #region Events
    emits: ['downloadstart', 'downloadended', 'unloaded', 'error', 'loaded'],
    // #endregion
    name: 'g-icon',
    // #region Props
    props: {
        src: {
            type: String,
            required: true,
        },
        title: {
            type: String,
        },
        transformSource: {
            type: Function,
            default: (svg) => svg,
        },
        keepDuringLoading: {
            type: Boolean,
            default: true,
        },
        colorReplacements: {
            type: Array,
            required: false,
            default: null
        },
    },
    // #endregion
    // #region Data
    data: () => ({
        svgElSource: null,
    }),
    // #endregion
    // #region Boostrap
    watch: {
        src(newValue) {
            this.getSource(newValue);
        },
    },
    mounted() {
        this.getSource(this.src);
    },
    // #endregion
    // #region Computed
    computed: {
        isVisible() {
            if (utils.isEmpty(this.src)) {
                return false;
            }
            return true;
        },
        rawInnerHtml() {
            if (!this.svgElSource) {
                return null;
            }
            return this.getSvgContent(this.svgElSource);
        },
        rawAttributes() {
            if (!this.svgElSource) {
                return filterAttrs(this.$attrs);
            }
            return Object.assign(this.getSvgAttrs(this.svgElSource), filterAttrs(this.$attrs));
        }
    },
    //#endregion
    // #region Core
    methods: {
        getSvgAttrs(svgEl) {
            // copy attrs
            let svgAttrs = {};
            const attrs = svgEl.attributes;
            if (!attrs) {
                return svgAttrs;
            }
            for (let i = attrs.length - 1; i >= 0; i--) {
                svgAttrs[attrs[i].name] = attrs[i].value;
            }
            return svgAttrs;
        },
        getSvgContent(svgEl) {
            //Data
            svgEl = svgEl.cloneNode(true);
            svgEl = this.transformSource(svgEl);
            //Run
            if (this.title) {
                setTitle(svgEl, this.title);
            }
            if (this.colorReplacements) {
                //Vars
                let tHtml = svgEl.innerHTML;
                //Run
                this.colorReplacements.forEach((c) => {
                    if (!c.target) {
                        return false;
                    }
                    if (c.target.startsWith('#')) {
                        tHtml = tHtml.replaceAll(c.source, c.target);
                    }
                    if (c.target.startsWith('--')) {
                        tHtml = tHtml.replaceAll(c.source, 'var(' + c.target + ')');
                    }
                });
                return tHtml;
            }
            else {
                return svgEl.innerHTML;
            }
        },
        getSource(src) {
            // fill cache by src with promise
            if (!cache[src]) {
                // download
                cache[src] = this.download(src);
            }
            // notify svg is unloaded
            if (this.svgElSource && cache[src].isPending() && !this.keepDuringLoading) {
                this.svgElSource = null;
                this.$emit('unloaded');
            }
            // inline svg when cached promise resolves
            cache[src]
                .then((svg) => {
                this.svgElSource = svg;
                // wait to render
                this.$nextTick(() => {
                    // notify
                    this.$emit('loaded', this.$el);
                });
            })
                .catch((err) => {
                // notify svg is unloaded
                if (this.svgElSource) {
                    this.svgElSource = null;
                    this.$emit('unloaded');
                }
                // remove cached rejected promise so next image can try load again
                delete cache[src];
                this.$emit('error', err);
            });
        },
        /**
            * Get the contents of the SVG
            * @param {string} url
            * @returns {Promise<Element>}
            */
        download(url) {
            return makePromiseState(new Promise((resolve, reject) => {
                const request = new XMLHttpRequest();
                request.open('GET', url, true);
                request.onloadstart = () => {
                    this.$emit('downloadstart');
                };
                request.onloadend = () => {
                    this.$emit('downloadended');
                };
                request.onload = () => {
                    if (request.status >= 200 && request.status < 400) {
                        try {
                            // Setup a parser to convert the response to text/xml in order for it to be manipulated and changed
                            const parser = new DOMParser();
                            const result = parser.parseFromString(request.responseText, 'text/xml');
                            let svgEl = result.getElementsByTagName('svg')[0];
                            if (svgEl) {
                                // svgEl = this.transformSource(svgEl);
                                resolve(svgEl);
                            }
                            else {
                                reject(new Error('Loaded file is not valid SVG"'));
                            }
                        }
                        catch (e) {
                            reject(e);
                        }
                    }
                    else {
                        reject(new Error('Error loading SVG'));
                    }
                };
                request.onerror = reject;
                request.send();
            }));
        },
    }
    // #endregion
});
